import { useState } from 'react';
import { BookOpen, Search, Route as RouteIcon, CreditCard, Mail, Menu, MessageCircle, X } from 'lucide-react';
import { Link, useRouteLoaderData } from 'react-router';
import { ThemeToggle } from './ThemeToggle';
import { openChatWidget } from '~/lib/chat-visitor';

// Alle drie zijn sinds fase 4 echte React-routes (zie app/routes.ts), dus dit
// zijn <Link>'s geworden. Dat is geen kosmetiek: met <a href> gooide iedere
// klik op de hoofdnavigatie het hele document weg — wit scherm, opnieuw
// hydrateren, en een lopend chatgesprek dat alleen nog uit sessionStorage
// teruggehaald kon worden. Nu blijft de schil staan en vloeit alleen de
// inhoud over.
const NAV = [
  { href: '/', label: 'Kennisbank', icon: BookOpen },
  { href: '/search', label: 'Zoeken', icon: Search },
  { href: '/leerpaden', label: 'Leerpaden', icon: RouteIcon },
  { href: '/emulator', label: 'Emulator', icon: CreditCard },
];

const ITEM =
  'flex items-center gap-2 rounded-md px-3.5 py-2 text-base font-medium text-ink-2 transition hover:bg-surface-hover hover:text-ink';
const ITEM_MOBILE =
  'flex w-full items-center gap-3 rounded-md px-3 py-3 text-lg font-medium text-ink-2 transition hover:bg-surface-hover hover:text-ink';

export function SiteHeader() {
  const [open, setOpen] = useState(false);

  // Staat de chat aan? Komt uit de loader van de padloze publieke layout-route,
  // dus zonder extra verzoek — hetzelfde patroon als app/lib/me.ts:11. Staat hij
  // uit, dan blijft Contact gewoon een mailto: en verschijnt er nergens een
  // knop die niets doet.
  const layout = useRouteLoaderData('routes/public/layout') as { chatEnabled?: boolean } | undefined;
  const chatEnabled = layout?.chatEnabled === true;

  // kb-vt-chrome: de balk krijgt een eigen view-transition-name en valt zo
  // buiten de vloeier van de inhoud (theme.css, sectie 7). Hij is het enige
  // dat bij een paginawissel bewijsbaar NIET beweegt.
  return (
    <header className="kb-vt-chrome surface-glass sticky top-0 z-100 border-b border-hairline">
      <div className="mx-auto flex h-16 max-w-[var(--container-app)] items-center justify-between gap-4 px-5">
        {/* Geen hover-effect op het logo. hover:opacity-85 liet het merk-oranje
            naar het wit eronder wegzakken, en dat leest als een ANDERE kleur in
            plaats van als een aanwijzing dat je kunt klikken. Een logo verkleurt
            niet. De aanwijzing komt van de cursor; de focusring uit de basislaag
            blijft staan voor toetsenbordgebruik. */}
        <Link to="/" viewTransition className="flex items-center" aria-label="LEZA Kennisbank — home">
          <img src="/leza-logo.png" alt="LEZA" className="h-10 w-auto" />
        </Link>

        {/* desktop */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="Hoofdnavigatie">
          {NAV.map(({ href, label, icon: Icon }) => (
            <Link key={href} to={href} viewTransition prefetch="intent" className={ITEM}>
              <Icon size={15} strokeWidth={2.2} aria-hidden />
              {label}
            </Link>
          ))}
          {chatEnabled ? (
            <button type="button" onClick={openChatWidget} className={ITEM}>
              <MessageCircle size={15} strokeWidth={2.2} aria-hidden />
              Contact
            </button>
          ) : (
            <a href="mailto:info@leza.nl" className={ITEM}>
              <Mail size={15} strokeWidth={2.2} aria-hidden />
              Contact
            </a>
          )}
          <span className="mx-2 h-5 w-px bg-hairline" aria-hidden />
          <ThemeToggle />
        </nav>

        {/* mobiel — dit bestond niet: de oude nav verdween onder 768px */}
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Menu sluiten' : 'Menu openen'}
            className="rounded-md p-2 text-ink-2 transition hover:bg-surface-hover"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Hoofdnavigatie"
          className="border-t border-hairline bg-surface px-4 py-2 md:hidden motion-safe:animate-fade-down"
        >
          {NAV.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              to={href}
              viewTransition
              prefetch="intent"
              onClick={() => setOpen(false)}
              className={ITEM_MOBILE}
            >
              <Icon size={18} strokeWidth={2} aria-hidden />
              {label}
            </Link>
          ))}
          {chatEnabled ? (
            <button
              type="button"
              onClick={() => { setOpen(false); openChatWidget(); }}
              className={ITEM_MOBILE}
            >
              <MessageCircle size={18} strokeWidth={2} aria-hidden />
              Contact
            </button>
          ) : (
            <a href="mailto:info@leza.nl" className={ITEM_MOBILE}>
              <Mail size={18} strokeWidth={2} aria-hidden />
              Contact
            </a>
          )}
        </nav>
      )}
    </header>
  );
}
